import React from 'react';
import { Helmet } from 'react-helmet';
import BreadcrumbSchema from '../components/BreadcrumbSchema';
import {
  Wrench,
  Truck,
  ShieldCheck,
  BadgePercent,
  Clock,
  MessageCircle,
} from 'lucide-react';

// WhatsApp config (edit phone number or default message here)
const WHATSAPP_PHONE = '522292072733';
const DEFAULT_MESSAGE =
  'Hola GENSOL, necesito cotizar reparación y logística para una torre de iluminación o soldadora. ¿Me pueden ayudar?';

const whatsappHref = `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(
  DEFAULT_MESSAGE
)}`;

const benefits = [
  {
    icon: Wrench,
    title: 'Servicio Técnico Especializado',
    text: 'Diagnóstico y reparación en taller con mecánicos calificados para torres de iluminación y soldadoras industriales.',
  },
  {
    icon: Truck,
    title: 'Logística Integral',
    text: 'Contamos con servicio de recolección, traslado, maniobras logísticas y entrega directa para recoger el equipo en tu obra o instalaciones. Nosotros nos encargamos de todo el movimiento.',
  },
  {
    icon: ShieldCheck,
    title: 'Mantenimiento Preventivo y Correctivo',
    text: 'Cambio de focos LED, reparación de mástil hidráulico, motor, sistema eléctrico y refacciones originales.',
  },
  {
    icon: Clock,
    title: 'Tiempos de Respuesta Rápidos',
    text: 'Reducimos el tiempo muerto de tu equipo con turnos prioritarios y refacciones en stock.',
  },
];

const ReparacionMantenimientoPage = () => {
  return (
    <>
      <Helmet>
        <title>
          Reparación y mantenimiento de torres luminarias y soldadoras | GENSOL
        </title>
        <meta
          name="description"
          content="Reparación y mantenimiento de torres luminarias y soldadoras en taller especializado. Recolección, traslado y entrega. Renta de equipo de respaldo con tarifa preferencial."
        />
        <meta
          name="keywords"
          content="reparación torres luminarias, mantenimiento torres de iluminación, reparación soldadoras, mantenimiento soldadoras, taller mecánico industrial, logística equipos, Veracruz"
        />
        <link
          rel="canonical"
          href="https://www.gensolver.com/servicios/reparacion-y-mantenimiento"
        />
        <meta
          property="og:title"
          content="Reparación y mantenimiento de torres luminarias y soldadoras | GENSOL"
        />
        <meta
          property="og:description"
          content="Reparación y mantenimiento en taller con logística integral. Equipo de respaldo con precio preferencial."
        />
        <meta
          property="og:url"
          content="https://www.gensolver.com/servicios/reparacion-y-mantenimiento"
        />
      </Helmet>

      <BreadcrumbSchema
        items={[
          { name: 'Inicio', url: 'https://www.gensolver.com/' },
          { name: 'Servicios', url: 'https://www.gensolver.com/servicios' },
          {
            name: 'Reparación y Mantenimiento',
            url: 'https://www.gensolver.com/servicios/reparacion-y-mantenimiento',
          },
        ]}
      />

      <main
        id="reparacion-y-mantenimiento"
        data-page="reparacion-y-mantenimiento"
        data-analytics-page="reparacion-y-mantenimiento"
        className="pt-24"
      >
        {/* Hero Banner with workshop image */}
        <section className="relative bg-gray-900 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://images.pexels.com/photos/4489732/pexels-photo-4489732.jpeg?auto=compress&cs=tinysrgb&w=1600"
              alt="Taller mecánico industrial GENSOL para reparación y mantenimiento de torres luminarias y soldadoras"
              title="Taller mecánico industrial GENSOL"
              className="w-full h-full object-cover opacity-40"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/30" />
          </div>

          <div className="relative container mx-auto px-4 py-20 md:py-28">
            <div className="max-w-3xl">
              <span
                data-testid="reparacion-eyebrow"
                className="inline-block bg-orange-500 text-white text-xs md:text-sm font-bold tracking-wide uppercase px-3 py-1 rounded-full mb-5"
              >
                Servicio Integral GENSOL
              </span>
              <h1
                data-testid="reparacion-page-title"
                className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-5"
              >
                Reparación y mantenimiento de{' '}
                <span className="text-orange-500">torres luminarias y soldadoras</span>
              </h1>
              <p className="text-base md:text-lg text-gray-200 mb-8 max-w-2xl">
                Taller especializado, mecánicos calificados y servicio de logística integral.
                Recogemos tu equipo, lo reparamos y te lo entregamos listo para operar.
              </p>

              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="reparacion-cta-hero"
                data-analytics-click="reparacion-whatsapp-hero"
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-3.5 rounded-xl transition-all duration-300 hover:scale-[1.03] shadow-lg shadow-orange-500/30"
              >
                <MessageCircle className="w-5 h-5" />
                Cotizar reparación y logística por WhatsApp
              </a>
            </div>
          </div>
        </section>

        {/* Benefits / Value proposition */}
        <section className="py-16 bg-gray-100">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                Propuesta de valor <span className="text-orange-500">logística y comercial</span>
              </h2>
              <p className="text-gray-700">
                En GENSOL no solo reparamos tu equipo: nos encargamos de todo el movimiento para que
                tu operación no se detenga.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              {benefits.map((b) => {
                const Icon = b.icon;
                return (
                  <article
                    key={b.title}
                    data-testid={`reparacion-benefit-${b.title.replace(/\s+/g, '-').toLowerCase()}`}
                    className="flex gap-4 bg-white p-6 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md hover:border-orange-500/40 transition-all duration-300"
                  >
                    <div className="shrink-0 w-12 h-12 rounded-xl bg-orange-500/15 text-orange-500 flex items-center justify-center">
                      <Icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1">
                        {b.title}
                      </h3>
                      <p className="text-gray-600">{b.text}</p>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* Backup equipment card (commercial offer) */}
        <section className="py-16 bg-gray-200">
          <div className="container mx-auto px-4">
            <article
              data-testid="reparacion-backup-card"
              className="relative overflow-hidden bg-gradient-to-br from-black via-gray-900 to-black rounded-3xl border border-orange-500/30 shadow-2xl p-8 md:p-12"
            >
              {/* decorative blur */}
              <div className="absolute -top-20 -right-20 w-72 h-72 bg-orange-500/30 rounded-full blur-3xl pointer-events-none" />

              <div className="relative grid md:grid-cols-[auto,1fr,auto] gap-6 md:gap-8 items-center">
                <div className="shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-orange-500 text-white flex items-center justify-center shadow-lg shadow-orange-500/40">
                  <BadgePercent className="w-9 h-9 md:w-10 md:h-10" />
                </div>

                <div className="text-white">
                  <span className="inline-block text-orange-400 text-xs md:text-sm font-bold tracking-wide uppercase mb-2">
                    Beneficio exclusivo
                  </span>
                  <h3 className="text-2xl md:text-3xl font-bold mb-3">
                    Renta de equipo de respaldo con{' '}
                    <span className="text-orange-500">precio preferencial</span>
                  </h3>
                  <p className="text-gray-300 max-w-2xl">
                    ¿Tu operación no puede detenerse? Solicita un equipo en renta con tarifa
                    preferencial exclusiva mientras reparamos tu torre de iluminación o soldadora.
                  </p>
                </div>

                <a
                  href={`https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(
                    'Hola GENSOL, mi equipo está en reparación. Necesito renta de equipo de respaldo con tarifa preferencial.'
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="reparacion-backup-cta"
                  data-analytics-click="reparacion-whatsapp-backup"
                  className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-3.5 rounded-xl transition-all duration-300 hover:scale-[1.03] whitespace-nowrap"
                >
                  <MessageCircle className="w-5 h-5" />
                  Solicitar equipo de respaldo
                </a>
              </div>
            </article>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 bg-gray-100">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              Listo para <span className="text-orange-500">reparar tu equipo</span>?
            </h2>
            <p className="text-gray-700 max-w-2xl mx-auto mb-8">
              Mándanos un mensaje con marca, modelo y ubicación de tu torre luminaria o soldadora.
              Te cotizamos el servicio completo: diagnóstico, reparación y logística.
            </p>
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reparacion-cta-final"
              data-analytics-click="reparacion-whatsapp-final"
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-7 py-4 rounded-xl transition-all duration-300 hover:scale-105 shadow-lg shadow-orange-500/30"
            >
              <MessageCircle className="w-5 h-5" />
              Cotizar reparación y logística por WhatsApp
            </a>
          </div>
        </section>
      </main>
    </>
  );
};

export default ReparacionMantenimientoPage;
